import { AppBar, Tab } from "@mui/material";
import { StyledComponentProps, withStyles } from "@mui/styles";
import { FeedClassKey, FeedStyleClasses, TabLargeStyle, TimelineClassKey, TimelineStyleClasses } from "assets/jss/common-styles";
import { AppBarTabs } from "components/AppTabs";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconCircle from "components/Icons/IconCircle";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { FeedType } from "models/ApiEnums";
import { FeedQuestionsFilter } from "models/ApiModels";
import * as React from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState } from "store/app/reducer";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import * as SideBarActions from 'store/sidebar/actions';
import CardManager from "views/Timeline/Components/CardManager";
import { ITimelineDispatchProps, TimelineWindowMessageListener } from "views/Timeline/TimelineCommons";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
    app: AppState;
}

interface IState {
    dialog: any;
}

interface IProps {
    timestamp?: Date;
}

interface IDispatchProps extends ITimelineDispatchProps {
    onGetFeed: (filter: FeedQuestionsFilter, startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IStateProps & IDispatchProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderMenu {
    category: FeedType;
    onChange: (event: React.ChangeEvent<{}>, value: any) => void;
}
const RenderMenuBar: React.FC<Props & IRenderMenu> = (props) => {
    const { t } = useTranslation();
    return (
        <div className={props.classes.filterTabContainer}>
            <AppBar position="static" color="default" classes={{ root: props.classes.appTabFilterRoot }}>
                <AppBarTabs
                    centered={true}
                    value={props.category}
                    onChange={props.onChange}
                >
                    <Tab
                        value={FeedType.QuestionRecent}
                        label={
                            <span>
                                {
                                    props.app.callouts.findIndex(p => p.feed == FeedType.QuestionRecent && p.callout === true) > -1 &&
                                    <MuiColor variant="warning"><IconCircle fixedWidth /></MuiColor>
                                }
                                <span>{t("Views.Timeline.TimelineQuestions.TabRecent")}</span>
                            </span>
                        }
                        style={TabLargeStyle}
                        disabled={props.feed.loading}
                    />

                    <Tab
                        value={FeedType.QuestionToYou}
                        label={
                            <span>
                                {
                                    props.app.callouts.findIndex(p => p.feed == FeedType.QuestionToYou && p.callout === true) > -1 &&
                                    <MuiColor variant="warning"><IconCircle fixedWidth /></MuiColor>
                                }
                                <span>{t("Views.Timeline.TimelineQuestions.TabToYou")}</span>
                            </span>
                        }
                        style={TabLargeStyle}
                        disabled={props.feed.loading}
                    />

                    <Tab
                        value={FeedType.QuestionFollowing}
                        label={
                            <span>
                                {
                                    props.app.callouts.findIndex(p => p.feed == FeedType.QuestionFollowing && p.callout === true) > -1 &&
                                    <MuiColor variant="warning"><IconCircle fixedWidth /></MuiColor>
                                }
                                <span>{t("Views.Timeline.TimelineQuestions.TabFollowing")}</span>
                            </span>
                        }
                        style={TabLargeStyle}
                        disabled={props.feed.loading}
                    />
                </AppBarTabs>
            </AppBar>
        </div>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////


class TimelineQuestionsComponent extends React.PureComponent<Props, IState> {

    private filter: FeedQuestionsFilter;
    private wmm: TimelineWindowMessageListener;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null
        };

        this.resetFilter();
    }

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        this.filter.category = value;
        this.feedReset();
    };

    ///
    private resetFilter() {
        this.filter = {
            category: FeedType.QuestionRecent
        };
    }

    ///
    public componentDidMount() {
        this.feedReset();
        this.wmm = new TimelineWindowMessageListener(this.props.executeFeedMessage);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.timestamp != this.props.timestamp && !this.props.feed.loading)
            this.feedReset();
    }

    ///
    public componentWillUnmount() {
        this.feedReset();
        this.wmm.dettach();
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, loading }, snackbar } = this.props;

        return (
            <div>
                <RenderMenuBar {...this.props} category={this.filter.category} onChange={this.handleTabSelection} />

                <div className={classes.timelineContainer}>

                    {this.state.dialog}

                    <CardManager
                        classes={classes}
                        feedType={this.filter.category}
                        feed={feed}
                        snackbar={snackbar}
                        onChangePublicationVisibility={this.props.onChangePublicationVisibility}
                        onExpandPublication={this.props.onExpandPublication}
                        onRemovePublication={this.props.onRemovePublication}
                        onReloadPublication={this.props.onReloadPublication}
                        onUpdatePublication={this.props.onUpdateFeedData}
                        onIgnoreActionRequest={this.props.onIgnoreActionRequest}
                        onSidebarResetRequest={this.props.onSidebarResetRequest}
                        onRemoveFromFeed={this.props.onRemoveFromFeed}
                        onGetFeed={(startIndex: number, endIndex: number) => { return this.props.onGetFeed(this.filter, startIndex, endIndex); }}
                        onFeedReset={() => this.props.onFeedReset()}
                    />

                    {
                        lastError != null
                            ? <RequestErrorReport error={lastError} />
                            : this.props.feed.data.length == 0 && !loading
                                ? <MuiMessageBox variant="info" message={this.props.t("Common.Feed.Filter.QuestionResultText_zero")} />
                                : null
                    }

                </div>
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feed,
        app: state.app
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.QuestionsFeedAction();

    return {
        onAddPublication: (publicationId) => dispatch(action.addPublication(publicationId)),
        onChangePublicationVisibility: (uid, publicationId, visibility) => dispatch(action.changePublicationVisibility(uid, publicationId, visibility)),
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onFeedReset: () => dispatch(action.feedReset()),
        onIgnoreActionRequest: (uid, publicationId, actionId) => dispatch(action.ignoreActionRequest(uid, publicationId, actionId)),

        onGetFeed: (filter, startIndex, endIndex) => dispatch(action.getFeed(filter, startIndex, endIndex)),

        onSidebarResetRequest: () => dispatch(SideBarActions.reset()),

        executeFeedMessage: (messageType) => action.executeFeedMessage(messageType, dispatch)
    };
}

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

const Timeline = connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(withAllStyles(TimelineQuestionsComponent))));

///////////////////////////////////////////////////////////////////////////

const TimelineQuestions: React.FC<Props> = () => {
    return (
        <Timeline
            timestamp={new Date()}
        />
    );
};

export default TimelineQuestions;